import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus({ state: "error", message: "Please share your name and email." });
      return;
    }
    setStatus({ state: "sending", message: "" });
    try {
      const res = await axios.post(`${API}/appointment-request`, form);
      setStatus({
        state: "success",
        message: res.data?.message || "Your request has been sent.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        message:
          detail ||
          "Something went wrong on our end. Please try again in a moment.",
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(closest-side, hsl(var(--primary)), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-6xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
            (05) &nbsp; Book a Session
          </span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1] text-foreground">
            Request an <em className="text-primary">appointment.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg text-foreground/75 leading-relaxed max-w-md">
            Write in a few words. Neha will read your message personally and
            reply from her own inbox — usually within a week.
          </p>

          <div className="mt-10 space-y-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              <span>Sessions run 1 hour, at the same day &amp; time weekly.</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              <span>Average wait time for a first slot: ~ one week.</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              <span>All conversations are held in complete confidence.</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          onSubmit={handleSubmit}
          data-testid={CONTACT.form}
          className="lg:col-span-7 bg-card border border-border rounded-[2.5rem] p-8 md:p-12 shadow-[0_30px_80px_-40px_rgba(54,46,41,0.25)]"
        >
          <div className="space-y-6">
            <Field
              label="Your name"
              name="name"
              value={form.name}
              onChange={handleChange}
              testid={CONTACT.name}
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              testid={CONTACT.email}
              required
            />
            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
                Briefly tell me what brings you here <span className="lowercase text-muted-foreground/70">(optional)</span>
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid={CONTACT.message}
                rows={5}
                placeholder="Take your time. A few sentences is more than enough."
                className="w-full bg-transparent border border-border rounded-2xl px-5 py-4 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:justify-between">
            <button
              type="submit"
              disabled={status.state === "sending"}
              data-testid={CONTACT.submit}
              className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-foreground text-background hover:bg-primary transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="text-sm md:text-base tracking-wide">
                {status.state === "sending" ? "Sending..." : "Send Request"}
              </span>
              <span className="w-10 h-10 rounded-full bg-background/15 flex items-center justify-center group-hover:rotate-45 transition-transform duration-500">
                <ArrowUpRight size={18} />
              </span>
            </button>

            {status.state === "success" && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={CONTACT.success}
                className="flex items-center gap-2 text-sm text-foreground/80"
              >
                <span className="w-6 h-6 rounded-full bg-secondary/60 flex items-center justify-center">
                  <Check size={14} />
                </span>
                {status.message}
              </motion.div>
            )}
            {status.state === "error" && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={CONTACT.error}
                className="text-sm text-primary"
              >
                {status.message}
              </motion.div>
            )}
          </div>

          <p className="mt-8 pt-6 border-t border-border text-xs md:text-sm text-muted-foreground leading-relaxed">
            Session fees and payment details will be coordinated directly once
            your appointment is confirmed. All payments are handled offline.
          </p>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-border rounded-2xl px-5 py-4 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
      />
    </div>
  );
}
